<template>
    <div>
        <p class="form-header">{{ $t('formHeader.formHeaderStep1') }}</p>
        <b-form
            ref="form"
            class="form"
            novalidate
            @submit.prevent="nextStep"
            @change="changeEditing"
        >
            <div class="container-form">
                <div class="first-col">
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.name') }} *</div>
                        <b-form-input
                            v-model="submission.firstName"
                            type="text"
                            class="input-box"
                            :state="textFieldState(submission.firstName)"
                            required
                            size="lg"
                            trim
                            @input="submission.firstName = submission.firstName.toUpperCase()"
                        />
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.surname') }} *</div>
                        <b-form-input
                            v-model="submission.lastName"
                            type="text"
                            class="input-box"
                            :state="textFieldState(submission.lastName)"
                            required
                            size="lg"
                            trim
                            @input="submission.lastName = submission.lastName.toUpperCase()"
                        />
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.address') }} *</div>
                        <b-form-input
                            v-model="submission.address"
                            type="text"
                            class="input-box"
                            :state="textFieldState(submission.address)"
                            name="address-line1"
                            required
                            size="lg"
                            @input="submission.address = submission.address.toUpperCase()"
                        />
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.city') }} *</div>
                        <b-form-input
                            v-model="submission.city"
                            type="text"
                            class="input-box"
                            :state="textFieldState(submission.city)"
                            name="address-level1"
                            required
                            size="lg"
                            @input="submission.city = submission.city.toUpperCase()"
                        />
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.state') }} *</div>
                        <b-form-input
                            v-if="locale === 'en'"
                            v-model="submission.state"
                            type="text"
                            class="input-box"
                            :state="textFieldState(submission.state)"
                            name="address-line2"
                            required
                            size="lg"
                            @input="submission.state = submission.state.toUpperCase()"
                        />
                        <b-form-select
                            v-else
                            v-model="submission.district"
                            class="select-menu"
                            :state="validDistrict"
                            required
                            @input="validateField(submission.district, 'validDistrict')"
                        >
                            <option
                                v-for="district in districtsOptions"
                                :key="district.id"
                                :value="{id: district.id}"
                                class="option-item"
                            >
                                {{ district[i18n('name')] }}
                            </option>
                        </b-form-select>
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.country') }} *</div>
                        <b-form-select
                            v-model="submission.country"
                            class="select-menu"
                            :state="validCountry"
                            required
                            @input="validateField(submission.country, 'validCountry')"
                        >
                            <b-form-select-option
                                v-for="country in countriesList"
                                :key="country.id"
                                :value="{id: country.id}"
                                class="option-item"
                            >
                                {{ country[i18n('name')] }}
                            </b-form-select-option>
                        </b-form-select>
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.phone') }} *</div>
                        <vue-phone-number-input
                            v-model="submission.phoneNumber"
                            class="phone-field"
                            :class="{'error-phone': validPhone}"
                            default-country-code="GR"
                            name="tel"
                            required
                            :translations="phoneNumberLocale"
                            @update="checkNumberValidation"
                            @input="validatePhoneNumber"
                        />
                        <small v-if="validPhone" class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </small>
                        <small v-if="submission.phoneNumber && !validNumber" class="error">
                            {{ $t('SubmissionFormStep1.invalidNumber') }}
                        </small>
                    </div>
                </div>
                <div class="second-col">
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.email') }} *</div>
                        <b-form-input
                            v-model="submission.email"
                            type="text"
                            class="input-box"
                            :state="emailFieldState(submission.email)"
                            name="email"
                            required
                            size="lg"
                        />
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.wrongEmailFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.gender') }} *</div>
                        <b-form-select
                            v-model="submission.sex"
                            class="select-menu"
                            :state="validGender"
                            required
                            @input="validateField(submission.sex, 'validGender')"
                        >
                            <option
                                v-for="sex in sexOptions"
                                :key="sex.value"
                                :value="sex.value"
                                class="option-item"
                            >
                                {{ sex[i18n('name')] }}
                            </option>
                        </b-form-select>
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.birthday') }} *</div>
                        <div class="custom-date">
                            <date-picker
                                v-model="submission.birthdayDate"
                                type="date"
                                class="date-picker"
                                name="bday"
                                :formatter="momentFormat"
                                :input-attr="{required: 'true'}"
                                @input="validateBirthday(submission.birthdayDate, 'validBirthday')"
                            />
                            <img
                                src="@/assets/icons/calendar.png"
                                class="custom-cal"
                                :class="{'reposition-custom-cal': validBirthday}"
                                alt="calendar icon"
                            >
                        </div>
                        <small
                            v-if="validBirthday"
                            class="error"
                        >
                            {{ $t('errors.emptyFieldError') }}
                        </small>
                    </div>
                    <div class="container-form-input">
                        <div class="label">{{ $t('SubmissionFormStep1.educationLevel') }} *</div>
                        <b-form-select
                            v-model="submission.educationLevel"
                            class="select-menu"
                            :state="validEducationLevel"
                            required
                            @input="validateField(submission.educationLevel, 'validEducationLevel')"
                        >
                            <option
                                v-for="level in educationOptions"
                                :key="level.id"
                                :value="{id: level.id}"
                            >
                                {{ level[i18n('name')] }}
                            </option>
                        </b-form-select>
                        <b-form-invalid-feedback class="error">
                            {{ $t('errors.emptyFieldError') }}
                        </b-form-invalid-feedback>
                    </div>
                    <div class="container-form-input">
                        <div class="label">
                            {{ $t('SubmissionFormStep1.professionalExperience') }} *
                        </div>
                        <b-form-input
                            v-model="submission.priorWorkExperience"
                            :number="true"
                            type="number"
                            required
                            :state="validateWorkExperience(false)"
                            class="input-box"
                            size="lg"
                            :min="0"
                            @keydown="noMinus($event)"
                        />
                        <b-form-invalid-feedback class="error">
                            {{ validateWorkExperience(true) ? $t('errors.emptyFieldError') : $t('errors.invalidWorkExperience') }}
                        </b-form-invalid-feedback>
                    </div>
                </div>
            </div>
            <div class="form-buttons">
                <button type="submit" class="submit">{{ $t('nextStep.nextStep') }}</button>
            </div>
        </b-form>
    </div>
</template>

<script>
    import countryService from '@/services/country-service'
    import educationLevelService from '@/services/education-level-service'
    import districtService from '@/services/district-service'
    import moment from 'moment'
    import DatePicker from 'vue2-datepicker'
    import 'vue2-datepicker/index.css'
    import VuePhoneNumberInput from 'vue-phone-number-input'
    import 'vue-phone-number-input/dist/vue-phone-number-input.css'
    import i18nMixin from '@/mixins/i18n-mixin'
    export default {
        name: 'SubmissionFormStep1',
        components: { DatePicker, VuePhoneNumberInput },
        mixins: [ i18nMixin ],
        data() {
            return {
                submission: {},
                validNumber: false,
                momentFormat: {
                    stringify: (date) => {
                        return date ? moment(date).format('DD/MM/YYYY') : ''
                    }
                },
                sexOptions: [
                    { value: 'MALE', nameGreek: 'Άντρας', nameEnglish: 'Male' },
                    { value: 'FEMALE', nameGreek: 'Γυναίκα', nameEnglish: 'Female' }
                ],
                phoneNumberLocale: {
                    phoneNumberLabel: ''
                },
                countriesList: [],
                educationOptions: [],
                districtsOptions: [],
                errorsFound: null,
                validDistrict: null,
                validCountry: null,
                validPhone: null,
                validGender: null,
                validBirthday: null,
                validEducationLevel: null
            }
        },
        computed: {
            locale() {
                return this.$root.$i18n.locale
            }
        },
        created(){
            countryService.fetchAllCountries().then((res) => {
                this.countriesList = res.content.sort(this.sortByOrder)
            })
            educationLevelService.fetchEducationLevels().then((res) => {
                this.educationOptions = res.content.sort(this.sortByOrder)
            })
            districtService.fetchAllDistricts().then((res) => {
                this.districtsOptions = res.content.sort(this.sortByOrder)
            })
        },

        beforeUpdate() {
            this.countriesList.sort(this.sortByOrder)
            this.educationOptions.sort(this.sortByOrder)
            this.districtsOptions.sort(this.sortByOrder)
        },

        methods: {
            changeEditing() {
                this.$parent.isEditing = true
            },
            nextStep() {
                this.validate()
                this.errorsFound = !this.$refs['form'].checkValidity()
                if(!this.errorsFound){
                    this.$emit('update', this.submission)
                }
            },
            validate() {
                this.validateField(this.submission.district, 'validDistrict')
                this.validateField(this.submission.country, 'validCountry')
                this.validateField(this.submission.sex, 'validGender')
                this.validateBirthday(this.submission.birthdayDate, 'validBirthday')
                this.validateField(this.submission.educationLevel, 'validEducationLevel')
                this.validatePhoneNumber()
            },
            validateField(field, flagName) {
                if(field === undefined || field === null) {
                    this[flagName] = false
                } else {
                    this[flagName] = true
                }
            },
            validatePhoneNumber() {
                const phone = this.submission.phoneNumber
                const phoneinValidConditions = phone === undefined || phone === null || phone === ''
                if(phoneinValidConditions) {
                    this.validPhone = true
                }else {
                    this.validPhone = false
                }
            },
            checkNumberValidation(event) {
                this.validNumber = event.isValid
            },
            emailFieldState(field) {
                if(this.errorsFound === null) {
                    if (field === undefined) {
                        return null
                    }
                } else {
                    if (field === undefined) {
                        return false
                    }
                }
                if (field.indexOf('@') === -1 || field.indexOf('.') === -1 || field.length < 5) {
                    return false
                }
                return true
            },
            textFieldState(field) {
                if(this.errorsFound === null) {
                    if (field === undefined) {
                        return null
                    }
                } else {
                    if (field === undefined) {
                        return false
                    }
                }
                if (field === null || field.length === 0) {
                    return false
                }
                return true
            },
            validateWorkExperience(checkingOnlyYearLimitations) {
                let flag = this.textFieldState(this.submission.priorWorkExperience)
                let limitations = this.submission.priorWorkExperience < 99 && this.submission.priorWorkExperience >= 0
                if(flag === null) {
                    return null
                } else if(checkingOnlyYearLimitations) {
                    const deletedField = this.submission.priorWorkExperience === ''
                    const emptyField = this.submission.priorWorkExperience === undefined
                    return deletedField || emptyField
                }else {
                    return flag && limitations
                }
            },
            validateBirthday(field, flagName) {
                const dateInput = document.querySelector('.mx-input')
                if(field === undefined || field === null) {
                    dateInput.style.borderColor='#dc3545'
                    dateInput.style.height='calc(1.5em + 1rem + 3.5px)'
                    this[flagName] = true
                } else {
                    dateInput.style.borderColor='#a0d4e9'
                    dateInput.style.height='calc(1.5em + 1rem + 2px)'
                    this[flagName] = false
                }
            },
            noMinus(e) {
                if (e.keyCode === 189 || e.keyCode === 173 || e.keyCode === 109) {
                    e.preventDefault()
                }
            },
            sortByOrder(a, b) {
                if (a.order === b.order) {
                    return this.sortByName(a,b)
                } else {
                    return b.order - a.order
                }
            }
        }
    }
</script>

<style>
.country-selector input {
    border: 0 !important;
    border-radius: 0 !important;
}

.input-tel input {
    border-left: 1px solid #a0d4e9 !important;
    border-top: 0 !important;
    border-right: 0 !important;
    border-bottom: 0 !important;
}

</style>

<style scoped>

.form-header {
    text-align: center;
    max-width: 540px;
    margin: 0 auto 1rem;
    line-height: 1.7rem;
    font-weight: 300;
    padding-left: 0.94rem;
    padding-right: 0.94rem;
}

.form {
    border-top: 1px solid #a0d4e9;
    max-width: 540px;
    margin: 0 auto;
}

.container-form {
    display: flex;
    flex-direction: column;
    padding: 1.5rem 0;
    border-bottom: 1px solid #a0d4e9;
    margin-bottom: 2rem;
}

.first-col,
.second-col {
    display: flex;
    flex-direction: column;
    width: 90%;
    margin: 0 auto;
}

.container-form-input {
    min-height: 6.6rem;
    margin-bottom: 0.5rem;
    max-width: 100%;
}

.label {
    color: #0089c2;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    font-weight: 300;
    font-family: 'Roboto', sans-serif;
    padding-right: 0.4rem;
}

/* Input styling starts here */

.select-menu {
    height: calc(1.5em + 1rem + 4px);
    cursor: pointer;
}

.select-menu,
.input-box {
    border-radius: 0;
    border-width: 2px;
    font-size: 1.0625rem;
}

.option-item:checked {
    background-color: #ddd;
}

.input-box {
    height: calc(1.5em + 1rem + 4px);
}

.phone-field {
    border: 2px solid #a0d4e9;
    height: calc(1.5em + 1rem + 6px);
}

.select-menu:not(.is-invalid),
.input-box:not(.is-invalid) {
    border-color: #a0d4e9;
}

.reposition-custom-cal {
    top: 2px !important;
    right: 2px !important;
}

.custom-date {
    height: calc(1.5em + 1rem + 4px);
    position: relative;
}

.custom-select select {
    width: 100%;
    height: 100%;
    padding: 0 0.75rem;
    background-color: #fff;
    font-size: 1.25rem;
    border: 0;
    appearance: none;
}

.custom-select {
    background-image: url("~@/assets/icons/arrow-up.png");
    background-repeat: no-repeat;
    background-size: 2.75rem 100%;
    background-position: right;
}

.custom-arrow,
.custom-cal {
    display: block;
    background: #a0d4e9;
    width: 2.85rem;
    pointer-events: none;
}

.custom-cal {
    position: absolute;
    top: 0;
    right: 0;
    height: 105%;
    padding: 0.75rem;
}

/* Form buttons start here */
.form-buttons {
    display: flex;
    justify-content: center;
}



.submit {
    border-radius: 0;
    border: 2px solid #a0d4e9;
    background-color: #fff;
    color: #0089c2;
    padding: 10px 50px;
    margin: 0 auto;
    font-weight: 300;
    font-size: 18px;
}

.submit:hover {
    transition: all 0.15s ease-in-out;
    color: #fff;
    background: #a0d4e9;
}

.error {
    color: #dc3545;
    width: 100%;
}

.error-phone {
    border: 2px solid #dc3545;
}

@media screen and (min-width: 768px) {
    .form,
    .form-header {
        max-width: 730px;
    }

    .first-col,
    .second-col {
        width: 100%;
    }

    .label {
        font-size: 1.1rem;
        width: 15ch;
        max-height: 45px;
    }

    .container-form-input {
        display: flex;
        flex-wrap: wrap;
        min-width: 500px;
        min-height: 71px;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
    }

    .input-box,
    .phone-field,
    .select-menu,
    .mx-datepicker {
        width: 530px;
    }

    .error {
        margin-top: -5px;
        margin-left: 200px;
    }
}

@media screen and (min-width: 992px) {
    .form,
    .form-header {
        max-width: 1100px;
    }

    .first-col {
        margin-right: 2rem;
    }

    .container-form-input {
        max-width: 520px;
        min-height: 80px;
        justify-content: center;
        margin-bottom: 0;
    }

    .label {
        font-size: 1rem;
    }

    .container-form {
        flex-direction: row;
    }

    .input-box,
    .phone-field,
    .select-menu,
    .mx-datepicker {
        width: 300px;
    }

    .error {
        margin-top: -16px;
        margin-left: 175px;
    }
}

</style>

<style>
.mx-datepicker {
    width: 100%;
}

.mx-input {
    border-radius: 0%;
    border: 2px solid #a0d4e9;
    height: calc(1.5em + 1rem + 2px);
    font-size: 1.25rem;
    padding: 0 0.75rem;
}

.mx-input:hover {
    border-color: #a0d4e9;
}
</style>
